/**
 * Media Devices store
 *
 * Holds a list of all current audio input, audio output and video input devices, the preferred devices and the currently selected devices,
 * and announces when a device has been removed or added.
 *
 * Preferred devices are stored as the browser's raw device label and resolved back to whatever deviceId carries that label right
 * now - see utilities/mediaDeviceLabels for why. The UI and SipClient keep working in deviceIds: the setters take one, the
 * resolvers return one.
 */

import { computed, watch, ref } from 'vue'
import { defineStore } from 'pinia'
import { useSettingsStore } from '@/stores/Settings'

import globalEvents from '@/utilities/GlobalEvents'
import logger from '@/utilities/Logger'
import _i18n from '@/utilities/I18n'
import { resolveWithin } from '@/utilities/Tools'
import { NO_CAMERA, isExcludedDevice, isNoCameraChosen, migratePreferredList, pushPreferredLabel, resolvePreferredDevice } from '@/utilities/mediaDeviceLabels'

import { MediaDeviceType, type MediaDevice, type MediaDeviceSettingsItem } from './MediaDevices.types'

/** How long to wait for getUserMedia before treating it as a device that will not answer */
const REQUEST_MEDIA_TIMEOUT = 20000

export const useMediaDevicesStore = defineStore('mediaDevices', () => {
	logger.debug('MediaDevices: Initializing store')

	const ss = useSettingsStore()
	const i18n = _i18n.global

	const devices = ref<MediaDevice[]>([])
	const hasAudioPermission = ref(false)
	const hasVideoPermission = ref(false)
	const _ready = ref(false)

	/** Is media devices is initialized and ready to use */
	const isReady = computed(() => _ready.value)

	globalEvents.on('StatusRequest', () => {
		globalEvents.emit('StatusReport', { key: 'mediaDevices', value: _ready.value })
		globalEvents.emit('StatusReport', { key: 'mediaDevicesList', value: devices.value })
	})

	/** Initialize */
	async function init() {
		logger.info('MediaDevices: Initializing')
		const permission1 = await requestMedia(true, true)
		if (permission1) {
			logger.info('MediaDevices: Got access to audio and video')
			hasAudioPermission.value = true
			hasVideoPermission.value = true
			stopMedia(permission1)
		} else {
			const permission2 = await requestMedia(true, false)
			if (permission2) {
				logger.info('MediaDevices: Got access to audio only')
				hasAudioPermission.value = true
				stopMedia(permission2)
			} else {
				logger.warning('MediaDevices: Failed to get access to audio and video')
			}
		}

		navigator.mediaDevices.ondevicechange = () => { buildDeviceList() }
		await buildDeviceList(true)
		migratePreferredLists()
		logPreferredDevices()
		_ready.value = true
	}

	/** Returns when all data has been loaded and the module is ready to be used */
	function ready() {
		if (_ready.value) { return Promise.resolve() }
		return new Promise(resolve => {
			const unwatch = watch(_ready, value => {
				if (value) {
					unwatch()
					resolve(undefined)
				}
			})
		})
	}

	/**
	 * Request media streams and access
	 * @param audio True for any audio device, or the deviceId of a specific audio input device
	 * @param video true for any video device, or the deviceId of a specific video input device
	 * @param width Desired video width
	 * @param height Desired video height
	 */
	async function requestMedia(audio: boolean | string, video: boolean | string, width?: number, height?: number) {
		// Request permission to access audio/video devices (getUserMedia) by initializing them
		const config: MediaStreamConstraints = { audio: false, video: false }

		if (typeof audio === 'boolean') { config.audio = audio }
		if (typeof video === 'boolean') { config.video = video }
		// Electron ignores bare deviceId constraints (they are only "ideal" hints), so require the device explicitly
		if (typeof audio === 'string') { config.audio = { deviceId: { exact: audio } } }
		if (typeof video === 'string') { config.video = { deviceId: { exact: video } } }
		if (width && height) {
			if (typeof config.video === 'boolean') {
				config.video = { width: { ideal: width }, height: { ideal: height } }
			} else if (typeof config.video === 'object') {
				config.video.width = { ideal: width }
				config.video.height = { ideal: height }
			}
		}

		try {
			// getUserMedia stays pending for as long as a permission prompt is unanswered, and a device that
			// refuses to open can leave it pending for good - everything waiting on it would stop there
			// A grant that turns up after we gave up would otherwise leave the microphone open behind us
			const stream = await resolveWithin(navigator.mediaDevices.getUserMedia(config), REQUEST_MEDIA_TIMEOUT, null, late => { if (late) { stopMedia(late) } })

			if (!stream) { logger.error(`MediaDevices: RequestMedia got no answer within ${REQUEST_MEDIA_TIMEOUT / 1000} seconds - giving up on it`) }

			return stream
		}
		catch (err) {
			logger.error(`MediaDevices: RequestMedia failed: ${(err as Error).message}`)
			return null
		}
	}

	/**
	 * Stop all tracks on a media stream
	 * @param mediaStream Media stream
	 */
	function stopMedia(mediaStream: MediaStream) {
		try {
			const tracks = mediaStream.getTracks()

			for (const track of tracks) {
				if (track) { track.stop() }
			}
		} catch (_) { /* Ignore */ }
	}

	/**
	 * Build list of devices
	 * @param initial True on initial run, false when invoked because of device change
	 * @private
	 */
	async function buildDeviceList(initial = false) {
		logger.info('MediaDevices: Building list of devices')

		try {
			// Build list of devices as it looks like now
			const rawDevices = await navigator.mediaDevices.enumerateDevices() // deviceId, kind, label, groupId
			const foundDevices: MediaDevice[] = []

			for (const device of rawDevices) {
				if (device.deviceId === 'communications') { continue }

				const hasProductId = RegExp(/\([a-fA-F0-9]{4}:[a-fA-F0-9]{4}\)/).exec(device.label)
				const productId = hasProductId ? hasProductId[0].slice(1, -1) : undefined
				const foundDevice: MediaDevice = {
					deviceId: device.deviceId,
					kind: device.kind as MediaDeviceType,
					label: device.deviceId === 'default' ? 'Standard enhed' : cleanDeviceName(device.label),
					groupId: device.groupId,
					productId: productId ?? '',
					rawLabel: device.label
				}

				foundDevices.push(foundDevice)
				logger.debug(`MediaDevices: Found device: ID=${foundDevice.deviceId} KIND=${foundDevice.kind} LABEL=${foundDevice.label} RAW=${foundDevice.rawLabel}`)
			}

			if (!initial) {
				// Device(s) has changed - find which one
				const removed = devices.value.filter(currentDevice => foundDevices.find(foundDevice => foundDevice.deviceId === currentDevice.deviceId) == null)
				const added = foundDevices.filter(foundDevice => devices.value.find(currentDevice => currentDevice.deviceId === foundDevice.deviceId) == null)

				devices.value = foundDevices

				for (const device of removed) {
					logger.debug(`MediaDevices: Device removed: ${device.deviceId} - ${device.label} - ${device.kind} - ${device.productId}`)
					globalEvents.emit('MediaDeviceChange', { event: 'removed', ...device })
				}

				for (const device of added) {
					logger.debug(`MediaDevices: Device added: ${device.deviceId} - ${device.label} - ${device.kind} - ${device.productId}`)
					globalEvents.emit('MediaDeviceChange', { event: 'added', ...device })
				}
			} else {
				devices.value = foundDevices
				globalEvents.emit('MediaDeviceSelected', { type: 'audioinput' })
			}
			logger.debug(`MediaDevices: Build list of devices with ${devices.value.length} devices`)
		}
		catch (err) {
			logger.warning(`MediaDevices: Build list failed: ${(err as Error).message}`)
		}
	}

	// #region Public

	/**
	 * Get the name/label of a device
	 * @param id Id of device
	 * @returns Name of device, null if not found
	 */
	function getDeviceName(id: string) {
		const device = devices.value.find(device => device.deviceId === id)

		return device?.label ?? null
	}

	/** The microphone to use: the first stored label that is present, else a headset, else none (the system default) */
	const microphoneDevice = computed(() => resolvePreferredDevice(ss.settings.media.preferredAudioInputs, devices.value, MediaDeviceType.audioinput, { headsetFallback: true }))

	/** Id of the microphone to use, 'default' when neither a stored device nor a headset is present */
	const microphoneDeviceId = computed(() => microphoneDevice.value?.deviceId ?? 'default')

	/** List of microphone devices - for settings */
	const audioInDevices = computed(() => {
		const selected = microphoneDeviceId.value

		return devices.value
			.filter(device => device.kind === MediaDeviceType.audioinput)
			.sort((a, b) => a.label.localeCompare(b.label))
			.map(o => { return {
				value: o.deviceId,
				label: o.label,
				group: o.groupId,
				selected: o.deviceId === selected,
				disabled: isExcludedDevice(o)
			} }) as MediaDeviceSettingsItem[]
	})

	/** The speaker to use: the first stored label that is present, else a headset, else none (the system default) */
	const speakerDevice = computed(() => resolvePreferredDevice(ss.settings.media.preferredAudioOutputs, devices.value, MediaDeviceType.audiooutput, { headsetFallback: true }))

	/** Id of the speaker to use, 'default' when neither a stored device nor a headset is present */
	const speakerDeviceId = computed(() => speakerDevice.value?.deviceId ?? 'default')

	/** List of speaker devices - for settings */
	const audioOutDevices = computed(() => {
		const selected = speakerDeviceId.value

		return devices.value
			.filter(device => device.kind === MediaDeviceType.audiooutput)
			.sort((a, b) => a.label.localeCompare(b.label))
			.map(device => { return {
				value: device.deviceId,
				label: device.label,
				group: device.groupId,
				selected: device.deviceId === selected,
				disabled: isExcludedDevice(device)
			} }) as MediaDeviceSettingsItem[]
	})

	/** The speaker to ring on: the first stored label that is present, else none (the system default) - never a headset by guess */
	const ringerDevice = computed(() => resolvePreferredDevice(ss.settings.media.preferredRingerOutputs, devices.value, MediaDeviceType.audiooutput, { headsetFallback: false }))

	/** Id of the speaker to ring on, 'default' when no stored device is present */
	const ringerDeviceId = computed(() => ringerDevice.value?.deviceId ?? 'default')

	/**
	 * The camera to use: none after "Benyt ikke", else the first stored label that is present, else the first camera -
	 * a camera never chosen means the default one
	 */
	const videoDevice = computed(() => {
		const preferred = ss.settings.media.preferredVideoInputs

		if (isNoCameraChosen(preferred)) { return undefined }
		return resolvePreferredDevice(preferred, devices.value, MediaDeviceType.videoinput, { headsetFallback: false, firstDeviceFallback: true })
	})

	/** Id of the camera to use, null after "Benyt ikke" or when there is no camera */
	const videoDeviceId = computed(() => videoDevice.value?.deviceId ?? null)

	/**
	 * Set when the chosen camera is missing and another one is used instead: the name of the missing one - the one chosen
	 * last - and the camera used, for the "... is no longer available, now using ..." message
	 */
	const videoDeviceFallback = computed(() => {
		const preferred = ss.settings.media.preferredVideoInputs
		const missing = preferred[0]
		const using = videoDevice.value

		if (!missing || !using || isNoCameraChosen(preferred)) { return undefined }
		if (resolvePreferredDevice(preferred, devices.value, MediaDeviceType.videoinput, { headsetFallback: false })) { return undefined }
		return { missing: cleanDeviceName(missing), using }
	})

	/** list of video input devices - for settings */
	const videoInDevices = computed(() => {
		const selected = videoDeviceId.value
		const list: MediaDeviceSettingsItem[] = devices.value
			.filter(device => device.kind === MediaDeviceType.videoinput)
			.sort((a, b) => a.label.localeCompare(b.label))
			.map(device => { return {
				value: device.deviceId,
				label: device.label,
				group: device.groupId,
				selected: device.deviceId === selected,
				disabled: isExcludedDevice(device)
			} })

		list.unshift({ value: NO_CAMERA, label: 'Benyt ikke', group: undefined, selected: selected == null, disabled: false })
		return list
	})

	const audioInputs = computed(() => { return devices.value.filter(device => device.kind === MediaDeviceType.audioinput) })
	const audioOutputs = computed(() => { return devices.value.filter(device => device.kind === MediaDeviceType.audiooutput) })
	const videoInputs = computed(() => { return devices.value.filter(device => device.kind === MediaDeviceType.videoinput) })

	/** Returns list of device groups for settings */
	const deviceGroups = computed(() => {
		const list: Record<string, { groupId: string, name: string, devices: MediaDevice[] }> = {}

		for (const dev of devices.value) {
			if (dev.label === 'Standard enhed') { continue }
			if (!list[dev.groupId]) {
				list[dev.groupId] = { groupId: dev.groupId, name: dev.label, devices: [] }
			}
			list[dev.groupId]!.devices.push(dev) // eslint-disable-line @typescript-eslint/no-non-null-assertion
		}

		return Object.values(list)
	})

	/** Returns the ringer volume */
	const ringerVolume = computed(() => ss.settings.media.ringerVolume)

	/**
	 * Find the enumerated device behind an id the user picked, if it may become a preferred device
	 * @param id Id of the device as the UI knows it
	 * @param kind Kind the device must be
	 * @param what What is being selected, for the log
	 * @returns The device, undefined when it is unknown or excluded (system default, communications, virtual)
	 */
	function selectableDevice(id: string, kind: MediaDeviceType, what: string) {
		const device = devices.value.find(device => device.deviceId === id && device.kind === kind)

		if (!device) {
			logger.info(`MediaDevices: Ignoring ${what} selection of unknown device id ${id}`)
			return undefined
		}
		if (isExcludedDevice(device)) {
			logger.info(`MediaDevices: Ignoring ${what} selection of excluded (default/virtual) device ${device.rawLabel} (${id})`)
			return undefined
		}
		return device
	}

	function setMicrophoneDeviceId(id?: string) {
		if (!id) { return }
		const device = selectableDevice(id, MediaDeviceType.audioinput, 'microphone')
		if (!device) { return }

		logger.info(`MediaDevices: Selected microphone ${device.rawLabel} (${id})`)
		ss.settings.media.preferredAudioInputs = pushPreferredLabel(ss.settings.media.preferredAudioInputs, device.rawLabel)
		globalEvents.emit('MediaDeviceSelected', { type: 'audioinput' })
	}

	function setSpeakerDeviceId(id?: string) {
		if (!id) { return }
		const device = selectableDevice(id, MediaDeviceType.audiooutput, 'speaker')
		if (!device) { return }

		logger.info(`MediaDevices: Selected speaker ${device.rawLabel} (${id})`)
		ss.settings.media.preferredAudioOutputs = pushPreferredLabel(ss.settings.media.preferredAudioOutputs, device.rawLabel)
		globalEvents.emit('MediaDeviceSelected', { type: 'audiooutput' })
	}

	function setVideoDeviceId(id: string) {
		if (!id || id === NO_CAMERA) {
			// Stored as a choice of its own: an empty list means no camera was ever chosen, which uses the first camera
			logger.info('MediaDevices: Selected no video device')
			ss.settings.media.preferredVideoInputs = [NO_CAMERA]
			globalEvents.emit('MediaDeviceSelected', { type: 'videoinput' })
			return
		}
		const device = selectableDevice(id, MediaDeviceType.videoinput, 'video')
		if (!device) { return }

		logger.info(`MediaDevices: Selected video ${device.rawLabel} (${id})`)
		ss.settings.media.preferredVideoInputs = pushPreferredLabel(ss.settings.media.preferredVideoInputs.filter(label => label !== NO_CAMERA), device.rawLabel)
		globalEvents.emit('MediaDeviceSelected', { type: 'videoinput' })
	}

	function setRingerDeviceId(id: string) {
		if (!id) {
			globalEvents.emit('MediaDeviceSelected', { type: 'ringeroutput' })
			return
		}
		const device = selectableDevice(id, MediaDeviceType.audiooutput, 'ringer')
		if (!device) { return }

		logger.info(`MediaDevices: Selected ringer ${device.rawLabel} (${id})`)
		ss.settings.media.preferredRingerOutputs = pushPreferredLabel(ss.settings.media.preferredRingerOutputs, device.rawLabel)
		globalEvents.emit('MediaDeviceSelected', { type: 'ringeroutput' })
	}

	function setRingerVolume(volume: number) {
		logger.info(`MediaDevices: Set ringer volume to ${volume}`)
		if (volume >= 0 && volume <= 1) { ss.settings.media.ringerVolume = volume }
		globalEvents.emit('MediaDeviceSelected', { type: 'ringervolume' })
	}

	/**
	 * Reset the preferred microphone, speaker and camera lists so each keeps only the device in use.
	 * A list whose device is the system default (nothing stored or a headset present) is emptied.
	 * "Benyt ikke" for the camera stays. The ringer preference is left untouched.
	 */
	function resetPreferredDevices() {
		logger.info('MediaDevices: Resetting preferred devices to current selection')

		ss.settings.media.preferredAudioInputs = microphoneDevice.value ? [microphoneDevice.value.rawLabel] : []
		ss.settings.media.preferredAudioOutputs = speakerDevice.value ? [speakerDevice.value.rawLabel] : []
		if (!isNoCameraChosen(ss.settings.media.preferredVideoInputs)) {
			ss.settings.media.preferredVideoInputs = videoDevice.value ? [videoDevice.value.rawLabel] : []
		}

		globalEvents.emit('MediaDeviceSelected', { type: 'audioinput' })
		globalEvents.emit('MediaDeviceSelected', { type: 'audiooutput' })
		globalEvents.emit('MediaDeviceSelected', { type: 'videoinput' })
	}

	/**
	 * Bring preferred lists written by an older version (deviceIds) up to date with labels, now that the devices are
	 * enumerated and can be looked up. A list is only reassigned when it changed, so the settings watcher does not
	 * save on every startup.
	 */
	function migratePreferredLists() {
		const media = ss.settings.media
		const migrate = (what: string, list: string[], kind: MediaDeviceType) => {
			const next = migratePreferredList(list, devices.value, kind)

			if (next.length === list.length && next.every((label, index) => label === list[index])) { return list }
			logger.info(`MediaDevices: Migrated preferred ${what} to labels: [${list.join(' | ')}] -> [${next.join(' | ')}]`)
			return next
		}

		media.preferredAudioInputs = migrate('microphones', media.preferredAudioInputs, MediaDeviceType.audioinput)
		media.preferredAudioOutputs = migrate('speakers', media.preferredAudioOutputs, MediaDeviceType.audiooutput)
		media.preferredVideoInputs = migrate('cameras', media.preferredVideoInputs, MediaDeviceType.videoinput)
		media.preferredRingerOutputs = migrate('ringers', media.preferredRingerOutputs, MediaDeviceType.audiooutput)
	}

	/**
	 * Describe a resolved device for the log
	 * @param device The device, or undefined when none resolved
	 * @param none What "none" means for this kind
	 */
	function describeDevice(device: MediaDevice | undefined, none: string) {
		return device ? `${device.rawLabel} (${device.deviceId})` : none
	}

	/** Log each preferred list and what it resolves to - labels are readable, so the whole list goes in */
	function logPreferredDevices() {
		const media = ss.settings.media

		logger.info(`MediaDevices: Preferred microphones [${media.preferredAudioInputs.join(' | ')}] -> ${describeDevice(microphoneDevice.value, 'none (system default)')}`)
		logger.info(`MediaDevices: Preferred speakers [${media.preferredAudioOutputs.join(' | ')}] -> ${describeDevice(speakerDevice.value, 'none (system default)')}`)
		logger.info(`MediaDevices: Preferred ringers [${media.preferredRingerOutputs.join(' | ')}] -> ${describeDevice(ringerDevice.value, 'none (system default)')}`)
		logger.info(`MediaDevices: Preferred cameras [${media.preferredVideoInputs.join(' | ')}] -> ${describeDevice(videoDevice.value, 'none')}`)
	}

	// A device coming or going, or a new selection, changes what resolves - say so, it is the first thing to look for
	// when a call ends up on the wrong device
	watch([microphoneDeviceId, speakerDeviceId, ringerDeviceId], () => {
		if (!_ready.value) { return }
		logger.info(`MediaDevices: Now using microphone ${describeDevice(microphoneDevice.value, 'system default')}, ` +
			`speaker ${describeDevice(speakerDevice.value, 'system default')}, ringer ${describeDevice(ringerDevice.value, 'system default')}`)
	})

	// #endregion

	function cleanDeviceName(a: string) {
		a = a.replace('Microphone', '')
			.replace('Mikrofonmatrice', '')
			.replace('Integrated', i18n.t('mediaDevices.builtin'))
			.replace('Camera', '')
			.replace('Speakers', '')
			.replace('Speaker', '')
			.replace('Headset Earphone', '')
			.replace('Headset Speaker', '')
			.replace('Headset', '')
			.replace('Communications', i18n.t('mediaDevices.communication'))
			.replace('Virtual', i18n.t('mediaDevices.virtual'))
			.replace('Internal', i18n.t('mediaDevices.builtin'))
			.replace('(Built-in)', '')
			.replace('Default', i18n.t('mediaDevices.default'))

		if (!a.startsWith('Mikrofon i')) { a = a.replace('Mikrofon', '') }

		const m1 = RegExp(/ \([0-9a-fA-F]{4}:[0-9a-fA-F]{4}\)$/).exec(a)
		if (m1) { a = a.substring(0, m1.index) }
		a = a.replace(/ {2}/g, ' ').trim()

		if (a.startsWith('(') && a.endsWith(')')) { a = a.slice(0, -1) }
		if (a.startsWith('(')) { a = a.substring(1) }
		if (RegExp(/^\d- \w/).exec(a)) { a = a.substring(3) }
		return a
	}

	logger.debug('MediaDevices: Store created')

	return { devices, hasAudioPermission, hasVideoPermission, isReady,
		microphoneDeviceId, microphoneDevice, audioInDevices, speakerDeviceId, speakerDevice, audioOutDevices, videoDeviceId, videoDevice, videoDeviceFallback, videoInDevices,
		ringerDeviceId, ringerDevice, ringerVolume, audioInputs, audioOutputs, videoInputs, deviceGroups,
		init, ready, getDeviceName, setMicrophoneDeviceId, setSpeakerDeviceId, setVideoDeviceId, setRingerDeviceId, setRingerVolume, resetPreferredDevices
	}
})
